import React, { useRef, memo, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { toPng } from 'html-to-image';
import { isEqual } from 'lodash';
import { ControlChart, ControlChartProps } from './ControlChart';
import { chartLog } from 'src/utils/logger';

const CaptureDiv = styled.div`
  position: fixed;
  width: 200px;
  height: 200px;
  z-index: -500;
  top: 0;
  left: 0;
  pointer-events: none;
  background: white;
`;

interface ControlChartCaptureProps extends Omit<ControlChartProps, 'isPrintable'> {
  onImageCaptured: (imageData: string) => void;
  disabled?: boolean;
}

export const ControlChartCapture: React.FC<ControlChartCaptureProps> = memo(
  ({ value, width = 86, height = 86, onImageCaptured, disabled = false }) => {
    const chartRef = useRef<HTMLDivElement>(null);

    const captureChart = useCallback(async () => {
      if (!chartRef.current || disabled) return;

      try {
        await new Promise((resolve) => setTimeout(resolve, 300));

        const dataUrl = await toPng(chartRef.current, {
          cacheBust: false,
          quality: 1,
          backgroundColor: 'white',
          pixelRatio: 2,
          width: width,
          height: height,
          skipFonts: true,
        });

        onImageCaptured(dataUrl);
      } catch (err) {
        chartLog.error('failed to capture chart', err);
      }
    }, [disabled, width, height, onImageCaptured]);

    useEffect(() => {
      if (value && !disabled) {
        captureChart();
      }
    }, [value, captureChart]);

    if (!value || disabled) return null;

    return (
      <CaptureDiv ref={chartRef}>
        <ControlChart value={value} isPrintable={true} width={width} height={height} />
      </CaptureDiv>
    );
  },
  isEqual,
);

export default ControlChartCapture;
